import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { fishHealthApi } from "@/api/fishHealthApi"
import { FishHealthForm } from "../components/FishHealthForm"
import { FormPageLayout } from "@/shared/components/forms"
import { PageLoading } from "@/shared/components/ui/LoadingSpinner"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { useFarmFromUrl } from "@/shared/hooks/useUrlSync"
import { pondsApi } from "@/features/ponds/api/pondsApi"
import { queryKeys } from "@/shared/api/queryClient"
import { buildRoute } from "@/shared/lib/constants"
import { toApiRequest, type FishHealthFormData } from "../schemas/fishHealthSchema"

export function EditFishHealthPage() {
  const { checkId } = useParams<{ checkId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  const { farmId, isLoading: isFarmLoading } = useFarmFromUrl()
  const pondId = searchParams.get("pondId")

  // Fetch pond data using query params
  const { data: pond, isLoading: isPondLoading } = useQuery({
    queryKey: queryKeys.ponds.detail(farmId || "", pondId || ""),
    queryFn: () => pondsApi.get(farmId!, pondId!),
    enabled: !!farmId && !!pondId,
  })

  // Fetch existing check data
  const { data: check, isLoading: isCheckLoading } = useQuery({
    queryKey: ["fish-health", farmId, pondId, checkId],
    queryFn: () => fishHealthApi.getCheck(farmId!, pondId!, checkId!),
    enabled: !!farmId && !!pondId && !!checkId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: FishHealthFormData) => {
      if (!farmId || !pondId || !checkId) throw new Error("Farm, pond, and check required")
      return fishHealthApi.update(farmId, pondId, checkId, toApiRequest(data))
    },
    onSuccess: () => {
      if (pondId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.fishHealth.all(pondId) })
      }
      if (farmId && pondId && checkId) {
        queryClient.invalidateQueries({
          queryKey: ["fish-health", farmId, pondId, checkId],
        })
      }
      handleCancel()
    },
  })

  const handleCancel = () => {
    if (farmId && checkId) {
      navigate(buildRoute.fishHealthDetail(farmId, checkId, pondId))
    } else if (farmId) {
      navigate(buildRoute.fishHealth(farmId, pondId))
    }
  }

  const handleSubmit = async (data: FishHealthFormData) => {
    await updateMutation.mutateAsync(data)
  }

  if (isFarmLoading || isPondLoading || isCheckLoading) {
    return <PageLoading message="Loading fish health check..." />
  }

  if (!check) {
    return (
      <Card>
        <div className="text-center py-16">
          <h3 className="font-roboto text-base font-semibold text-gray-900">
            Check not found
          </h3>
          <p className="font-roboto text-sm text-gray-600 mt-2">
            The fish health check you're trying to edit doesn't exist.
          </p>
          <Button onClick={handleCancel} className="mt-4">
            Back to Fish Health
          </Button>
        </div>
      </Card>
    )
  }

  // Convert API response to form default values
  // Parse parasite codes from parasiteName if available
  const parasiteCodes = check.parasiteName
    ? check.parasiteName.split(",").map((c) => c.trim()).filter(Boolean)
    : check.parasiteCode
    ? [check.parasiteCode]
    : []

  const defaultValues: Partial<FishHealthFormData> = {
    feedType: check.feedType || "",
    averageWeight: check.averageWeight,
    sampleSize: check.sampleSize,
    checkedAt: new Date(check.checkedAt),
    mortalityCount: check.mortalityCount ?? undefined,
    parasiteCodes,
    behaviorObservations: check.behaviorObservations ?? "",
    physicalSymptoms: check.physicalSymptoms ?? "",
    treatmentApplied: check.treatmentApplied ?? "",
    veterinarianNotes: check.veterinarianNotes ?? "",
    imageIds: check.imageIds ?? [],
  }

  return (
    <FormPageLayout
      title="Edit Fish Health Check"
      subtitle={pond?.name}
      onCancel={handleCancel}
      onSubmit={() => {
        document.getElementById("fish-health-form")?.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        )
      }}
      isSubmitting={updateMutation.isPending}
      submitLabel="Save Changes"
      cancelLabel="Cancel"
    >
      <FishHealthForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
      />
    </FormPageLayout>
  )
}
